import { Button } from '../../components/Button';
import { FileWarning } from '../../icons/lucide';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { DARK_LAYER1 } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { SurfaceEmptyState } from '../SurfaceEmptyState';
import type { DocumentLoadFailedProps } from './DocumentLoadFailed.types';

/**
 * A document whose TEXT could not be loaded (offline with a cold cache, a
 * 5xx, a network blip, an extraction the worker gave up on) — ONE treatment
 * on both clients, driven by the shared `sourceExtractionState === 'failed'`.
 *
 * Only the document surface tells the failure: cards, study and quizzes keep
 * working beside it. Retry asks for the text again; a success removes this.
 *
 * Two placements of the same block, picked by what the platform can still
 * show (see `DocumentLoadFailedProps.layout`): web replaces the document with
 * the `pane` card, mobile lays the `inline` notice over a PDF that is still
 * readable.
 *
 * Labels arrive pre-translated (the DS carries no i18n).
 */
export function DocumentLoadFailed({
  layout = 'pane',
  title,
  description,
  retryLabel,
  onRetry,
  className,
  testID,
}: DocumentLoadFailedProps) {
  const { isDark } = useTheme();

  if (layout === 'inline') {
    return (
      <View
        className={cn(
          'w-full flex-row items-center gap-3 rounded-2xl border border-secondary-200 bg-secondary-50 px-4 py-3',
          // Laid over the page, so it floats at SURFACE level in dark — never
          // the near-black `secondary-50` mirrors to.
          DARK_LAYER1,
          className,
        )}
        accessibilityRole="alert"
        testID={testID}>
        <FileWarning size={20} color={brandColor('amber', '700', isDark)} />
        <View className="min-w-0 flex-1 shrink flex-col gap-0.5">
          <Text className="text-b2 font-semibold text-subtitle-800 native:font-mona-semibold">
            {title}
          </Text>
          {description ? (
            <Text className="text-b3 font-medium text-body-600 native:font-mona-medium">
              {description}
            </Text>
          ) : null}
        </View>
        <Button
          variant="secondary"
          size="s"
          onPress={onRetry}
          webProps={{ type: 'button' }}>
          {retryLabel}
        </Button>
      </View>
    );
  }

  return (
    <View className={cn('h-full w-full flex-1 p-2', className)}>
      <SurfaceEmptyState
        testID={testID}
        artwork={<FileWarning size={48} color={brandColor('secondary', '400', isDark)} />}
        title={title}
        description={description}
        action={
          <Button
            variant="secondary"
            size="m"
            fullWidth
            onPress={onRetry}
            webProps={{ type: 'button' }}>
            {retryLabel}
          </Button>
        }
      />
    </View>
  );
}
(DocumentLoadFailed as { displayName?: string }).displayName = 'DocumentLoadFailed';
